import React from 'react';
import { observer } from 'mobx-react-lite';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { OutlineSegment, OutlineSegmentCollection, ClippingPolygonType, WallType, isWallType, WallConnectionStart, WallConnectionEnd, WallCornerKey, WallShapeType } from '../../types/floorplannerTypes';
import { calculateWallImaginaryLines, getEndpointImaginaryIntersections } from '../../utils/imaginaryLineHelpers';
import { getCornerPoint } from '../../utils/wallCornerHelpers';

// Types
interface EdgeConnection {
  sourceWallId: string;
  targetWallId: string;
  sourceEndpoint: string;
  targetEndpoint: string;
  sourceSide: string;
  targetSide: string;
  sourceEdgePoint: { x: number; y: number };
  targetEdgePoint: { x: number; y: number };
  gapDistance?: number;
  connectionAngle?: number;
}

interface JointPolygon {
  vertices: { x: number; y: number; z?: number }[];
  fillColor: string;
  zIndex: number;
}

interface Wall {
  id: string;
  start: { x: number; y: number };
  end: { x: number; y: number };
  wallWidth?: number;
  width: number;
  color: string;
  edgeConnections: EdgeConnection[];
  connections: any[];
  cutouts: any[];
  path: { x: number; y: number }[];
}

interface ImaginaryIntersectionDebugInfo {
  wallId: string;
  endpoint: "start" | "end";
  targetWallId: string;
  leftPoint: THREE.Vector2;
  rightPoint: THREE.Vector2;
}

interface DebugVisualizationProps {
  floorplannerStore: any;
  editorStore: any;
  renderStore: any;
}

// Pure component (no observer) - Edge Point Markers
const EdgePointMarkersView: React.FC<{ walls: Wall[] }> = ({ walls }) => {
  return (
    <group>
      {walls.map((wall: Wall) =>
        wall.edgeConnections.map((conn: EdgeConnection, index: number) => (
          <React.Fragment key={`edge-${wall.id}-${index}`}>
            {/* Source edge point - RED */}
            <group position={[conn.sourceEdgePoint.x, conn.sourceEdgePoint.y, 0.015]}>
              <mesh>
                <circleGeometry args={[0.05, 16]} />
                <meshBasicMaterial color="red" />
              </mesh>
            </group>

            {/* Edge normal arrow */}
            <line>
              <bufferGeometry>
                <bufferAttribute
                  attach="attributes-position"
                  args={[new Float32Array([
                    conn.sourceEdgePoint.x,
                    conn.sourceEdgePoint.y,
                    0.015,
                    conn.sourceEdgePoint.x + (conn.sourceSide === 'left' ? -0.3 : 0.3),
                    conn.sourceEdgePoint.y,
                    0.015
                  ]), 3]}
                />
              </bufferGeometry>
              <lineBasicMaterial color="red" linewidth={2} />
            </line>

            {/* Target edge point - BLUE */}
            <group position={[conn.targetEdgePoint.x, conn.targetEdgePoint.y, 0.015]}>
              <mesh>
                <circleGeometry args={[0.05, 16]} />
                <meshBasicMaterial color="blue" />
              </mesh>
            </group>
          </React.Fragment>
        ))
      )}
    </group>
  );
};

// Pure component (no observer) - Joint Polygon Markers
const JointPolygonMarkersView: React.FC<{ polygons: [string, JointPolygon][] }> = ({ polygons }) => {
  return (
    <group>
      {polygons.map(([jointId, polygon]) => {
        const positions: number[] = [];
        polygon.vertices.forEach((vertex: { x: number; y: number }) => {
          positions.push(vertex.x, vertex.y, 0.015);
        });
        // Close the polygon
        if (polygon.vertices.length > 0) {
          positions.push(polygon.vertices[0].x, polygon.vertices[0].y, 0.015);
        }

        return (
          <React.Fragment key={`joint-${jointId}`}>
            {/* Polygon outline */}
            <line>
              <bufferGeometry>
                <bufferAttribute
                  attach="attributes-position"
                  args={[new Float32Array(positions), 3]}
                />
              </bufferGeometry>
              <lineBasicMaterial color="green" linewidth={2} />
            </line>

            {/* Vertex markers */}
            {polygon.vertices.map((vertex: { x: number; y: number }, index: number) => (
              <group key={`vertex-${index}`} position={[vertex.x, vertex.y, 0.015]}>
                <mesh>
                  <circleGeometry args={[0.03, 8]} />
                  <meshBasicMaterial color="green" />
                </mesh>
              </group>
            ))}
          </React.Fragment>
        );
      })}
    </group>
  );
};

// Pure component (no observer) - Outline Segment Markers
const OutlineSegmentMarkersView: React.FC<{ walls: Wall[]; outlineSegmentsMap: Map<string, OutlineSegmentCollection> }> = ({ walls, outlineSegmentsMap }) => {
  return (
    <group>
      {walls.map((wall: Wall) => {
        const segmentCollection = outlineSegmentsMap.get(wall.id) as OutlineSegmentCollection | undefined;
        if (!segmentCollection) return null;

        const allSegments = [
          ...(segmentCollection.leftSegments || []),
          ...(segmentCollection.rightSegments || []),
          ...(segmentCollection.endCapSegments || [])
        ];

        return (
          <React.Fragment key={`wall-segments-${wall.id}`}>
            {allSegments.map((segment, index) => (
              <React.Fragment key={`segment-${index}`}>
                {/* Segment line */}
                <line>
                  <bufferGeometry>
                    <bufferAttribute
                      attach="attributes-position"
                      args={[new Float32Array([
                        segment.start.x, segment.start.y, 0.015,
                        segment.end.x, segment.end.y, 0.015
                      ]), 3]}
                    />
                  </bufferGeometry>
                  <lineBasicMaterial color={segment.color || '#333333'} linewidth={2} />
                </line>

                {/* Start point */}
                <group position={[segment.start.x, segment.start.y, 0.015]}>
                  <mesh>
                    <circleGeometry args={[0.03, 8]} />
                    <meshBasicMaterial color="#333333" />
                  </mesh>
                </group>

                {/* End point */}
                <group position={[segment.end.x, segment.end.y, 0.015]}>
                  <mesh>
                    <circleGeometry args={[0.03, 8]} />
                    <meshBasicMaterial color="#333333" />
                  </mesh>
                </group>
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      })}
    </group>
  );
};

// Pure component (no observer) - Wall Length Markers
const WallLengthMarkersView: React.FC<{ walls: Wall[]; defaultWallWidth: number }> = ({ walls, defaultWallWidth }) => {
  return (
    <group>
      {walls.map((wall: Wall) => (
        <React.Fragment key={`wall-markers-${wall.id}`}>
          {/* Start point */}
          <group position={[wall.start.x, wall.start.y, 0.02]}>
            <mesh>
              <circleGeometry args={[0.025, 16]} />
              <meshBasicMaterial color="orange" />
            </mesh>
          </group>

          {/* End point */}
          <group position={[wall.end.x, wall.end.y, 0.02]}>
            <mesh>
              <circleGeometry args={[0.025, 16]} />
              <meshBasicMaterial color="purple" />
            </mesh>
          </group>
          {(() => {
            const dx = wall.end.x - wall.start.x;
            const dy = wall.end.y - wall.start.y;
            const length = Math.hypot(dx, dy);
            // Calculate the exact midpoint of the wall
            const midX = (wall.start.x + wall.end.x) / 2;
            const midY = (wall.start.y + wall.end.y) / 2;
            const angle = Math.atan2(dy, dx);
            const wallWidth = wall.wallWidth ?? wall.width ?? defaultWallWidth ?? 0.15;
            const fontSize = (Math.max(Math.min(wallWidth * 0.7, 0.22), 0.05) / 3) * 2;
            // Position text at the center of the wall's thickness
            const textY = midY - (wallWidth / 4); // Subtract to move text down toward center
            const text = `${wall.id} · ${length.toFixed(2)}m`;

            return (
              <Text
                position={[midX, textY, 0.5]}
                rotation={[0, 0, angle]}
                anchorX="center"
                anchorY="middle"
                textAlign="center"
                fontSize={fontSize}
                color="#000000"
                outlineWidth={0.0005}
                outlineColor="#ffffff"
                outlineOpacity={0.8}
                strokeOpacity={0}
                strokeWidth={0}
                strokeColor="#000000"
                depthOffset={10}
                renderOrder={9999}
                onSync={(troika) => {
                  if (troika?.material) {
                    troika.material.depthTest = false;
                    troika.material.depthWrite = false;
                    troika.material.transparent = true;
                    troika.material.needsUpdate = true;
                  }
                }}
              >
                {text}
              </Text>
            );
          })()}
        </React.Fragment>
      ))}
    </group>
  );
};

const ImaginaryLineDebugView: React.FC<{ walls: WallType[]; defaultWallWidth: number }> = ({ walls, defaultWallWidth }) => {
  if (walls.length === 0) {
    return null;
  }

  return (
    <group>
      {walls.map(wall => {
        const lines = calculateWallImaginaryLines(wall, wall.wallWidth ?? defaultWallWidth);
        const entries = [
          { key: `${wall.id}-left`, line: lines.left, color: '#00FFFF' },
          { key: `${wall.id}-right`, line: lines.right, color: '#FF00FF' },
        ];

        return (
          <React.Fragment key={`imaginary-${wall.id}`}>
            {entries.map(entry => (
              <React.Fragment key={entry.key}>
                <line>
                  <bufferGeometry>
                    <bufferAttribute
                      attach="attributes-position"
                      args={[new Float32Array([
                        entry.line.start.x, entry.line.start.y, 0.02,
                        entry.line.end.x, entry.line.end.y, 0.02,
                      ]), 3]}
                    />
                  </bufferGeometry>
                  <lineBasicMaterial color={entry.color} linewidth={2} />
                </line>
                <group position={[entry.line.start.x, entry.line.start.y, 0.02]}>
                  <mesh>
                    <circleGeometry args={[0.03, 8]} />
                    <meshBasicMaterial color={entry.color} />
                  </mesh>
                </group>
                <group position={[entry.line.end.x, entry.line.end.y, 0.02]}>
                  <mesh>
                    <circleGeometry args={[0.03, 8]} />
                    <meshBasicMaterial color={entry.color} />
                  </mesh>
                </group>
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      })}
    </group>
  );
};

const ImaginaryIntersectionDebugView: React.FC<{ intersections: ImaginaryIntersectionDebugInfo[] }> = ({ intersections }) => (
  <group>
    {intersections.map((entry, index) => (
      <React.Fragment key={`imaginary-intersection-${entry.wallId}-${entry.endpoint}-${entry.targetWallId}-${index}`}>
        <line>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              args={[new Float32Array([
                entry.leftPoint.x, entry.leftPoint.y, 0.025,
                entry.rightPoint.x, entry.rightPoint.y, 0.025,
              ]), 3]}
            />
          </bufferGeometry>
          <lineBasicMaterial color="#00FFFF" linewidth={2} />
        </line>
        <group position={[entry.leftPoint.x, entry.leftPoint.y, 0.03]}>
          <mesh>
            <circleGeometry args={[0.035, 12]} />
            <meshBasicMaterial color="#00FFAA" />
          </mesh>
        </group>
        <group position={[entry.rightPoint.x, entry.rightPoint.y, 0.03]}>
          <mesh>
            <circleGeometry args={[0.035, 12]} />
            <meshBasicMaterial color="#FFAA00" />
          </mesh>
        </group>
      </React.Fragment>
    ))}
  </group>
);

const collectEndpointIntersectionDebug = (
  wall: WallType,
  floorplannerStore: any,
  defaultWallWidth: number
): ImaginaryIntersectionDebugInfo[] => {
  const entries: ImaginaryIntersectionDebugInfo[] = [];
  const seen = new Set<string>();

  const registerConnection = (targetWallId: string, endpoint: "start" | "end") => {
    const key = `${targetWallId}:${endpoint}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);

    const targetWall = floorplannerStore.wallsMap.get(targetWallId);
    if (!targetWall) {
      return;
    }

    const intersections = getEndpointImaginaryIntersections(
      wall,
      endpoint,
      targetWall,
      defaultWallWidth,
    );

    if (!intersections) {
      return;
    }

    entries.push({
      wallId: wall.id,
      endpoint,
      targetWallId,
      leftPoint: intersections.left,
      rightPoint: intersections.right,
    });
  };

  (wall.centerConnections || []).forEach(conn => {
    if (conn.sourcePosition === WallConnectionStart) {
      registerConnection(conn.id, "start");
    } else if (conn.sourcePosition === WallConnectionEnd) {
      registerConnection(conn.id, "end");
    }
  });

  (wall.edgeConnections || []).forEach(conn => {
    if (conn.sourceWallId !== wall.id) {
      return;
    }
    if (conn.sourceEndpoint === "start") {
      registerConnection(conn.targetWallId, "start");
    } else if (conn.sourceEndpoint === "end") {
      registerConnection(conn.targetWallId, "end");
    }
  });

  return entries;
};

// Pure component (no observer) - Clipping Polygon Markers
const ClippingPolygonMarkersView: React.FC<{ clippings: [string, ClippingPolygonType[]][] }> = ({ clippings }) => {
  return (
    <group>
      {clippings.map(([wallId, clippingArray]: [string, ClippingPolygonType[]]) =>
        clippingArray.map((clipping: ClippingPolygonType, index: number) => {
          if (clipping.objectType !== "wall") return null;

          const positions: number[] = [];
          clipping.polygon.forEach((point: THREE.Vector2) => {
            positions.push(point.x, point.y, 0.012);
          });
          // Close the polygon
          if (clipping.polygon.length > 0) {
            positions.push(clipping.polygon[0].x, clipping.polygon[0].y, 0.012);
          }

          return (
            <React.Fragment key={`clipping-${wallId}-${index}`}>
              {/* Polygon outline */}
              <line>
                <bufferGeometry>
                  <bufferAttribute
                    attach="attributes-position"
                    args={[new Float32Array(positions), 3]}
                  />
                </bufferGeometry>
                <lineBasicMaterial color="magenta" linewidth={2} />
              </line>

              {/* Corner points */}
              {clipping.polygon.map((point: THREE.Vector2, pointIndex: number) => (
                <group key={`point-${pointIndex}`} position={[point.x, point.y, 0.012]}>
                  <mesh>
                    <circleGeometry args={[0.03, 8]} />
                    <meshBasicMaterial color="magenta" />
                  </mesh>
                </group>
              ))}
            </React.Fragment>
          );
        })
      )}
    </group>
  );
};

const FillCornerMarkersView: React.FC<{
  selectedWalls: WallType[];
  renderStore: any;
}> = ({ selectedWalls, renderStore }) => {
  if (selectedWalls.length === 0) {
    return null;
  }

  const cornerColors: Record<WallCornerKey, string> = {
    topLeft: '#ff0000',
    topRight: '#00ff00',
    bottomRight: '#0000ff',
    bottomLeft: '#ffff00',
  };

  const cornerOrder: WallCornerKey[] = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];

  return (
    <group>
      {selectedWalls.map((wall) => {
        const wallShapes = renderStore.innerWallShapesMap.get(wall.id) as WallShapeType[] | undefined;
        const innerShape = wallShapes?.find(shape => shape.lineSide === 'inner');
        if (!innerShape) {
          return null;
        }

        return (
          <React.Fragment key={`fill-corners-${wall.id}`}>
            {cornerOrder.map((cornerKey) => {
              const cornerPoint = getCornerPoint(innerShape, cornerKey, { clone: true });
              if (!cornerPoint) {
                return null;
              }

              return (
                <group key={`${wall.id}-${cornerKey}`} position={[cornerPoint.x, cornerPoint.y, 0.03]}>
                  <mesh>
                    <circleGeometry args={[0.03, 16]} />
                    <meshBasicMaterial color={cornerColors[cornerKey]} />
                  </mesh>
                </group>
              );
            })}
          </React.Fragment>
        );
      })}
    </group>
  );
};

const WallRectDebugView: React.FC<{ walls: WallType[]; defaultWallWidth: number }> = ({ walls, defaultWallWidth }) => {
  if (!walls.length) {
    return null;
  }

  const buildRect = (wall: WallType): THREE.Vector2[] | null => {
    const baseWidth = wall.wallWidth ?? defaultWallWidth;
    const pad = 0.02;
    const expandedWidth = baseWidth + pad * 2;
    const dir = new THREE.Vector2().subVectors(wall.end, wall.start);
    const len = dir.length();
    if (len < 1e-6 || expandedWidth <= 0) {
      return null;
    }
    const u = dir.clone().normalize();
    const perp = new THREE.Vector2(-u.y, u.x).multiplyScalar(expandedWidth / 2);
    const startPad = wall.start.clone().sub(u.clone().multiplyScalar(pad));
    const endPad = wall.end.clone().add(u.clone().multiplyScalar(pad));
    const a = startPad.clone().add(perp);
    const b = endPad.clone().add(perp);
    const c = endPad.clone().sub(perp);
    const d = startPad.clone().sub(perp);
    return [a, b, c, d, a];
  };

  return (
    <group>
      {walls.map((wall) => {
        const rect = buildRect(wall);
        if (!rect) return null;
        const positions: number[] = [];
        rect.forEach((p) => {
          positions.push(p.x, p.y, 0.05);
        });
        return (
          <line key={`wall-rect-${wall.id}`}>
            <bufferGeometry>
              <bufferAttribute
                attach="attributes-position"
                args={[new Float32Array(positions), 3]}
              />
            </bufferGeometry>
            <lineBasicMaterial color="orange" linewidth={2} />
          </line>
        );
      })}
    </group>
  );
};

// Main DebugVisualization component - ONLY observer here
export const DebugVisualization: React.FC<DebugVisualizationProps> = observer(
  ({ floorplannerStore, editorStore, renderStore }) => {
    // Only render if debug mode is active
    if (!editorStore.isDebugModeActive) {
      return null;
    }

    // Extract all data upfront while in observer context
    const walls = Array.from(floorplannerStore.wallsMap.values()) as Wall[];
    const polygons = Array.from(renderStore.jointPolygonsMap.entries()) as [string, JointPolygon][];
    const clippings = Array.from(renderStore.wallClippingsMap.entries()) as [string, ClippingPolygonType[]][];
    const outlineSegmentsMap = renderStore.outlineSegmentsMap;

    // Capture all debug flags
    const showEdgeConnections = editorStore.debugEdgeConnections;
    const showJointPolygons = editorStore.debugJointPolygons;
    const showOutlineSegments = editorStore.debugOutlineSegments;
    const showWallLengths = editorStore.debugWallLengths;
    const showClippingPolygons = editorStore.debugClippingPolygons;
    const showImaginaryLines = editorStore.debugImaginaryLines;
    const showImaginaryIntersections = editorStore.debugImaginaryIntersections;
    const showFillCorners = editorStore.debugFillCorners;
    const showWallRects = showEdgeConnections || showFillCorners;

    const selectedWalls = editorStore.selections.filter((selection: unknown): selection is WallType => isWallType(selection));
    const uniqueSelectedWalls: WallType[] = Array.from(
      new Map<string, WallType>(selectedWalls.map((wall: WallType) => [wall.id, wall])).values()
    );

    const intersectionDebugData = showImaginaryIntersections
      ? uniqueSelectedWalls.flatMap(wall =>
          collectEndpointIntersectionDebug(wall, floorplannerStore, floorplannerStore.wallWidth)
        )
      : [];

    return (
      <group>
        {showEdgeConnections && <EdgePointMarkersView walls={walls} />}
        {showJointPolygons && <JointPolygonMarkersView polygons={polygons} />}
        {showOutlineSegments && <OutlineSegmentMarkersView walls={walls} outlineSegmentsMap={outlineSegmentsMap} />}
        {showWallLengths && <WallLengthMarkersView walls={walls} defaultWallWidth={floorplannerStore.wallWidth} />}
        {showClippingPolygons && <ClippingPolygonMarkersView clippings={clippings} />}
        {showImaginaryLines && walls.length > 0 && (
          <ImaginaryLineDebugView walls={walls as unknown as WallType[]} defaultWallWidth={floorplannerStore.wallWidth} />
        )}
        {showImaginaryIntersections && intersectionDebugData.length > 0 && (
          <ImaginaryIntersectionDebugView intersections={intersectionDebugData} />
        )}
        {showWallRects && (
          <WallRectDebugView walls={uniqueSelectedWalls.length > 0 ? uniqueSelectedWalls : (walls as unknown as WallType[])} defaultWallWidth={floorplannerStore.wallWidth} />
        )}
        {showFillCorners && uniqueSelectedWalls.length > 0 && (
          <FillCornerMarkersView selectedWalls={uniqueSelectedWalls} renderStore={renderStore} />
        )}
      </group>
    );
  }
);

export default DebugVisualization;
